import React, { useState, useEffect, useCallback } from 'react'
import { Button, Card } from 'react-daisyui'
import { API } from '../../utils/api'
import { Api } from '../../utils/apiServer'
import DefaultLogo from "../../assets/default-logo.png"
import { useNavigate } from 'react-router-dom'
import { AiOutlineEnvironment, AiOutlineShop, AiOutlineTag } from "react-icons/ai"
import { BsShop } from "react-icons/bs"

function Merchant() {
    const [merchants, setMerchants] = useState([])
    const [page, setPage] = useState(1)
    const [lastPage, setLastPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)

    const navigate = useNavigate()
    const loadMerchants = useCallback(async () => {
        const isAppending = page > 1
        if (isAppending) setLoadingMore(true)
        else setLoading(true)

        const params = {
            access: 'auth',
            method: API.MERCHANT.type,
            url: API.MERCHANT.url,
            reqBody: {
                page: page,
                rows: 10
            }
        }
        try {
            const response = await Api(params)
            if (response?.status) {
                const incomingMerchants = Array.isArray(response?.data?.data) ? response.data.data : []
                setMerchants((currentMerchants) => {
                    if (!isAppending) return incomingMerchants

                    const currentPartnerIds = new Set(currentMerchants.map((merchant) => merchant.partner_id))
                    return [
                        ...currentMerchants,
                        ...incomingMerchants.filter((merchant) => !currentPartnerIds.has(merchant.partner_id)),
                    ]
                })
                setLastPage(Math.max(1, Number(response?.data?.lastPage) || 1))
            }
        } catch (error) {
            console.error('Error loading merchants:', error)
        } finally {
            if (isAppending) setLoadingMore(false)
            else setLoading(false)
        }
    }, [page])

    useEffect(() => {
        loadMerchants()
    }, [loadMerchants])

    const handleLoadMore = () => {
        if (loadingMore || page >= lastPage) return
        setPage((currentPage) => currentPage + 1)
    }

    // Loading skeleton component
    const LoadingSkeleton = () => (
        <div className="animate-pulse space-y-4">
            <div className="bg-gray-200 h-8 rounded w-1/3 mx-auto"></div>
            <div className="grid grid-cols-1 gap-4">
                {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-gray-200 h-32 rounded-lg"></div>
                ))}
            </div>
        </div>
    )

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="mx-auto max-w-sm px-5 py-6">
                    <LoadingSkeleton />
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="mx-auto max-w-sm px-5 py-6">
                <div className='flex flex-col space-y-6 scrollbar-hide'>

                    {/* Header Section */}
                    <div className="text-center space-y-2">
                        <div className="flex items-center justify-center space-x-2">
                            <BsShop className="text-2xl text-blue-600" />
                            <h1 className="text-2xl font-bold text-gray-800">Penyelenggara Voucher</h1>
                        </div>
                        <p className="text-gray-600 text-sm">Pilih partner untuk melihat voucher dan lokasi penukarannya.</p>
                        {merchants.length > 0 && (
                            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-3 rounded-lg border border-blue-100">
                                <div className="flex items-center justify-center space-x-2">
                                    <AiOutlineShop className="text-blue-600" />
                                    <span className="text-sm font-medium text-gray-700">
                                        {merchants.length} partner ditampilkan
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Merchant Grid */}
                    {merchants.length === 0 ? (
                        <Card className="bg-gradient-to-r from-gray-50 to-white border-2 border-dashed border-gray-300">
                            <Card.Body className="text-center py-12">
                                <BsShop className="text-6xl text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500 font-medium text-lg">Belum ada merchant</p>
                                <p className="text-gray-400 text-sm mt-1">Merchant akan segera hadir</p>
                            </Card.Body>
                        </Card>
                    ) : (
                        <div className="grid grid-cols-1 gap-4">
                            {merchants.map((merchant, i) => (
                                <Card
                                    key={i}
                                    className="border border-gray-100 bg-white shadow-sm transition-shadow duration-300 hover:shadow-lg"
                                >
                                    <Card.Body className="p-0">
                                        <div className="p-6">
                                            <div className="flex items-start space-x-4">
                                                {/* Merchant Logo */}
                                                <div className="flex-shrink-0">
                                                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center overflow-hidden border border-gray-200">
                                                        <img
                                                            src={merchant?.logo || DefaultLogo}
                                                            alt={merchant?.name || 'Merchant'}
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => {
                                                                e.target.src = DefaultLogo
                                                            }}
                                                        />
                                                    </div>
                                    </div>
                                    
                                                {/* Merchant Info */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="mb-2">
                                                        <button
                                                            type="button"
                                                            className="block max-w-full truncate text-left text-lg font-bold text-blue-600 underline-offset-2 transition-colors hover:text-blue-800 hover:underline focus:outline-none focus:underline"
                                                            onClick={() => navigate(`/merchant/${merchant.partner_id}`)}
                                                        >
                                                                {merchant?.name || 'Merchant Name'}
                                                        </button>
                                                    </div>
                                
                                                    {/* Merchant Stats */}
                                                    <div className="grid grid-cols-2 gap-3 mt-4">
                                                        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 rounded-lg">
                                                            <div className="flex items-center space-x-2">
                                                                <AiOutlineTag className="text-blue-600" />
                                                                <div>
                                                                    <p className="text-xs text-blue-700 font-medium">Voucher</p>
                                                                    <p className="text-sm font-bold text-blue-800">
                                                                        {merchant?.voucher_count || 0}
                                                                    </p>
                                                                </div>
                                                            </div>
                        </div>
                       
                                                        <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 rounded-lg">
                                                            <div className="flex items-center space-x-2">
                                                                <AiOutlineEnvironment className="text-green-600" />
                                                                <div>
                                                                    <p className="text-xs text-green-700 font-medium">Lokasi</p>
                                                                    <p className="text-sm font-bold text-green-800">
                                                                        {merchant?.redeem_merchants?.length || 0}
                                                                    </p>
                    </div>
                    </div>
                </div>
            </div>

                                                    {/* Description */}
                                                    {merchant?.description && (
                                                        <div className="mt-3 pt-3 border-t border-gray-100">
                                                            <p className="text-sm text-gray-600 line-clamp-2">
                                                                {merchant.description}
                                                            </p>
                                                        </div>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="mt-4 inline-flex items-center text-sm font-semibold text-blue-600 transition-colors hover:text-blue-800"
                                                        onClick={() => navigate(`/merchant/${merchant.partner_id}`)}
                                                    >
                                                        Lihat detail partner
                                                        <span aria-hidden="true" className="ml-1">→</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    )}

                    {merchants.length > 0 && page < lastPage ? (
                        <div className="pt-2">
                            <Button
                                type="button"
                                className="w-full border-0 bg-blue-600 font-semibold text-white hover:bg-blue-700"
                                onClick={handleLoadMore}
                                disabled={loadingMore}
                            >
                                {loadingMore ? 'Memuat partner...' : 'Tampilkan Lebih Banyak'}
                            </Button>
                        </div>
                    ) : null}

                    {merchants.length > 0 && page >= lastPage ? (
                        <p className="pb-2 text-center text-xs text-gray-500">Semua partner sudah ditampilkan</p>
                    ) : null}
                </div>
            </div>
        </div>
    )
}

export default Merchant
